import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertCircle, CheckCircle2, Circle, Clock, Download, ExternalLink, FileText, Image as ImageIcon,
  Trash2, Undo2, Upload, XCircle,
} from 'lucide-react';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import useLatestRequest from '../../hooks/useLatestRequest';
import StatusBadge from '../StatusBadge';
import CModal from '../shared/c-modal';
import UploadDocumentModal from './UploadDocumentModal';
import { documentTitle } from '../../utils/documentTitle';
import { formatDateOnly } from '../../utils/dateUtils';
import {
  canDeleteDocument, canReviewDocument, canUploadDocumentsFor, canWithdrawDocument,
} from '../../utils/documentPermissions';
import { safeHttpsLink } from '../../utils/safeLink';

const CHECKLIST_ICONS = {
  approved: { Icon: CheckCircle2, className: 'text-success-600' },
  pending: { Icon: Clock, className: 'text-warning-600' },
  rejected: { Icon: XCircle, className: 'text-danger-600' },
  missing: { Icon: Circle, className: 'text-gray-300' },
};

const formatBytes = (bytes) => {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SECONDARY_BUTTON = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-primary-500';

// A checklist category's status. A required category with nothing filed is called out;
// an optional one just reads as not uploaded.
const ChecklistStatus = ({ item }) => {
  if (item.status !== 'missing') return <StatusBadge status={item.status} type="document" />;
  return item.required
    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-50 text-danger-700">Missing</span>
    : <span className="text-xs text-gray-400">Not uploaded</span>;
};

/**
 * An employee's documents folder, shown in their profile (EmployeeDetail for HR and for the
 * employee themselves, and the employee's own Profile page): the required-document
 * checklist for their employment type, every document with its review status, and the
 * actions the caller may take. Every upload by the employee waits for HR approval; HR may
 * upload on an employee's behalf, approve or reject (with a reason) and delete. Each action
 * is offered on the server's own rule (utils/documentPermissions.js).
 *
 * The folder hides itself when the server refuses it (403), as the other per-employee
 * lists on the profile do. `onLoaded(rows)` reports the documents after every load.
 */
const EmployeeDocuments = ({ employeeId, employeeName, onLoaded, className = '' }) => {
  const { user, hasPermission } = useAuth();
  const location = useLocation();
  const sectionRef = useRef(null);
  const scrolledToHash = useRef(false);
  const beginRequest = useLatestRequest();
  // undefined while loading, the folder once loaded, null once refused, 'failed' on error.
  const [folder, setFolder] = useState();
  const [showUpload, setShowUpload] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [acting, setActing] = useState(null);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      const res = await api.get(`/api/documents/employee/${employeeId}/list`);
      if (!isCurrent()) return;
      const body = res.data || {};
      const rows = Array.isArray(body.data) ? body.data : [];
      setFolder({
        rows,
        checklist: Array.isArray(body.checklist) ? body.checklist : [],
        employmentType: body.employment_type,
        hasAccount: body.has_account !== false,
      });
      onLoadedRef.current?.(rows);
    } catch (error) {
      if (!isCurrent()) return;
      const refused = error.response?.status === 403 || error.response?.status === 404;
      setFolder(refused ? null : 'failed');
      onLoadedRef.current?.(null);
    }
  }, [employeeId, beginRequest]);

  useEffect(() => {
    load();
  }, [load]);

  // An old /documents link, or a profile link, lands on #documents: scroll there once the
  // folder has rendered.
  useEffect(() => {
    if (scrolledToHash.current || !folder || folder === 'failed') return;
    if (location.hash === '#documents' && sectionRef.current) {
      scrolledToHash.current = true;
      sectionRef.current.scrollIntoView?.({ block: 'start' });
    }
  }, [folder, location.hash]);

  if (folder === null) return null;

  const isSelf = user?.employee_id != null && String(user.employee_id) === String(employeeId);
  const canUpload = canUploadDocumentsFor(hasPermission, user, employeeId);

  const decide = async (doc, decision, reason) => {
    setActing(doc.id);
    try {
      if (decision === 'approve') {
        await api.put(`/api/documents/${doc.id}/approve`);
        toast.success('Document approved');
      } else {
        await api.put(`/api/documents/${doc.id}/reject`, { reason });
        toast.success('Document rejected');
        setRejecting(null);
        setRejectReason('');
      }
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${decision} the document`);
      // A 409 means someone else decided it meanwhile: show what it is now.
      if (error.response?.status === 409) await load();
    } finally {
      setActing(null);
    }
  };

  const remove = async (doc) => {
    const withdrawing = !canDeleteDocument(hasPermission, user, doc);
    setActing(doc.id);
    try {
      await api.delete(`/api/documents/${doc.id}`);
      toast.success(withdrawing ? 'Document withdrawn' : 'Document deleted');
      setDeleting(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${withdrawing ? 'withdraw' : 'delete'} the document`);
      // A 409 means HR decided it meanwhile: show what it is now.
      if (error.response?.status === 409) { setDeleting(null); await load(); }
    } finally {
      setActing(null);
    }
  };

  // The file comes through the authenticated route as a blob and is saved under its
  // original name; there is no public URL to link to.
  const download = async (doc) => {
    setActing(doc.id);
    try {
      const res = await api.get(`/api/documents/${doc.id}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_file_name || documentTitle(doc);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (error) {
      toast.error('Failed to download the document');
    } finally {
      setActing(null);
    }
  };

  const required = folder && folder !== 'failed' ? folder.checklist.filter((c) => c.required) : [];
  const deletingIsWithdraw = Boolean(deleting) && !canDeleteDocument(hasPermission, user, deleting);
  const approvedRequired = required.filter((c) => c.status === 'approved').length;

  return (
    <section
      id="documents"
      ref={sectionRef}
      data-testid="employee-documents"
      aria-labelledby="employee-documents-title"
      className={`bg-white rounded-lg shadow-soft p-6 scroll-mt-20 lg:scroll-mt-6 ${className}`.trim()}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h2 id="employee-documents-title" className="text-lg font-semibold text-gray-900">Documents</h2>
          {folder && folder !== 'failed' && required.length > 0 && (
            <p data-testid="employee-documents-summary" className="text-sm text-gray-500 mt-0.5">
              {approvedRequired} of {required.length} required document{required.length === 1 ? '' : 's'} approved
              {folder.employmentType ? ` (${folder.employmentType})` : ''}
            </p>
          )}
        </div>
        {canUpload && folder && folder !== 'failed' && folder.hasAccount && (
          <button
            type="button"
            data-testid="employee-documents-upload"
            onClick={() => setShowUpload(true)}
            className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            Upload document
          </button>
        )}
      </div>

      {folder === undefined && (
        <div className="space-y-2" aria-busy="true">
          <span className="sr-only">Loading documents</span>
          {[0, 1, 2].map((i) => <div key={i} className="h-10 rounded-md bg-gray-100 animate-pulse" />)}
        </div>
      )}

      {folder === 'failed' && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-danger-200 bg-danger-50 px-4 py-3">
          <p className="text-sm text-danger-700">Could not load the documents.</p>
          <button type="button" onClick={load} className={SECONDARY_BUTTON}>Try again</button>
        </div>
      )}

      {folder && folder !== 'failed' && (
        <>
          {!folder.hasAccount && (
            <div data-testid="employee-documents-no-account" className="flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-4 py-3 mb-5">
              <AlertCircle className="h-4 w-4 text-warning-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <p className="text-sm text-warning-800">
                This employee has no portal account yet, so no documents can be filed for them.
              </p>
            </div>
          )}

          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Checklist</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6" data-testid="employee-documents-checklist">
            {folder.checklist.map((item) => {
              const { Icon, className } = CHECKLIST_ICONS[item.status] || CHECKLIST_ICONS.missing;
              return (
                <li
                  key={item.category}
                  data-testid="employee-documents-checklist-item"
                  data-category={item.category}
                  data-status={item.status}
                  className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2"
                >
                  {/* The name wraps rather than truncating: a cut "Signed Employment ..." says less. */}
                  <span className="flex items-center gap-2 min-w-0">
                    <Icon className={`h-4 w-4 flex-shrink-0 ${className}`} aria-hidden="true" />
                    <span className="min-w-0 text-sm text-gray-900 break-words">
                      {item.category}
                      {item.required && (
                        <span className="ml-2 inline-block align-middle rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-primary-50 text-primary-700">
                          Required
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="flex-shrink-0"><ChecklistStatus item={item} /></span>
                </li>
              );
            })}
          </ul>

          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Files</h3>
          {folder.rows.length === 0 ? (
            <p data-testid="employee-documents-empty" className="text-sm text-gray-500 py-2">
              {canUpload && folder.hasAccount
                ? `No documents yet. Use Upload document to add ${isSelf ? 'yours' : 'the first one'}.`
                : 'No documents yet.'}
            </p>
          ) : (
            <ul className="divide-y divide-gray-200 border-y border-gray-200">
              {folder.rows.map((doc) => {
                const reviewable = canReviewDocument(hasPermission, user, doc);
                const deletable = canDeleteDocument(hasPermission, user, doc);
                // The owner's own pending upload: theirs to take back until HR decides.
                const withdrawable = !deletable && canWithdrawDocument(hasPermission, user, doc);
                const driveLink = safeHttpsLink(doc.google_drive_link);
                const busy = acting === doc.id;
                const isImage = (doc.mime_type || '').startsWith('image/');
                const DocIcon = isImage ? ImageIcon : FileText;
                const meta = [
                  // The title defaults to the category; say the category only when it adds something.
                  documentTitle(doc) !== doc.document_type ? doc.document_type : null,
                  `Uploaded ${formatDateOnly(doc.upload_date)}${doc.uploaded_by_name ? ` by ${doc.uploaded_by_name}` : ''}`,
                  formatBytes(doc.file_size_bytes),
                  doc.expiry_date ? `Expires ${formatDateOnly(doc.expiry_date)}` : null,
                ].filter(Boolean);
                return (
                  <li
                    key={doc.id}
                    data-testid="employee-documents-row"
                    data-document-id={doc.id}
                    data-status={doc.status}
                    // Actions sit beside the name only on a very wide screen: in the profile's
                    // two-thirds column they squeezed it into three lines.
                    className="py-4 flex flex-col 2xl:flex-row 2xl:items-start 2xl:justify-between gap-3"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <DocIcon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <p className="text-sm font-medium text-gray-900 break-words">{documentTitle(doc)}</p>
                          <StatusBadge status={doc.status} type="document" data-testid="employee-documents-status" />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 break-words">{meta.join(' · ')}</p>
                        {doc.status === 'approved' && doc.reviewed_by_name && (
                          <p className="text-xs text-gray-500 mt-0.5">Approved by {doc.reviewed_by_name}</p>
                        )}
                        {doc.status === 'rejected' && (
                          <p data-testid="employee-documents-rejection" className="text-xs text-danger-700 mt-1 break-words">
                            Rejected{doc.reviewed_by_name ? ` by ${doc.reviewed_by_name}` : ''}
                            {doc.rejection_reason ? `: ${doc.rejection_reason}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pl-8 2xl:pl-0 2xl:flex-shrink-0">
                      {doc.has_file && (
                        <button
                          type="button"
                          data-testid="employee-documents-download"
                          onClick={() => download(doc)}
                          disabled={busy}
                          className={SECONDARY_BUTTON}
                        >
                          <Download className="h-4 w-4" aria-hidden="true" />
                          Download
                        </button>
                      )}
                      {!doc.has_file && driveLink && (
                        <a
                          href={driveLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="employee-documents-drive-link"
                          aria-label={`Open ${documentTitle(doc)} in Google Drive`}
                          className={SECONDARY_BUTTON}
                        >
                          <ExternalLink className="h-4 w-4" aria-hidden="true" />
                          Open
                        </a>
                      )}
                      {/* A stored link that is not https:// is never made an href. */}
                      {!doc.has_file && doc.google_drive_link && !driveLink && (
                        <span data-testid="employee-documents-unsafe-link" className="text-xs text-gray-500 break-all">
                          Link not opened (not https): {doc.google_drive_link}
                        </span>
                      )}
                      {reviewable && (
                        <>
                          <button
                            type="button"
                            data-testid="employee-documents-approve"
                            onClick={() => decide(doc, 'approve')}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-white bg-success-600 hover:bg-success-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-success-500"
                          >
                            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                            Approve
                          </button>
                          <button
                            type="button"
                            data-testid="employee-documents-reject"
                            onClick={() => { setRejecting(doc); setRejectReason(''); }}
                            disabled={busy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-danger-300 text-danger-700 bg-white hover:bg-danger-50 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-danger-500"
                          >
                            <XCircle className="h-4 w-4" aria-hidden="true" />
                            Reject
                          </button>
                        </>
                      )}
                      {deletable && (
                        <button
                          type="button"
                          data-testid="employee-documents-delete"
                          onClick={() => setDeleting(doc)}
                          disabled={busy}
                          aria-label={`Delete ${documentTitle(doc)}`}
                          title="Delete"
                          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-400 hover:text-danger-600 hover:bg-danger-50 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-danger-500"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      )}
                      {withdrawable && (
                        <button
                          type="button"
                          data-testid="employee-documents-withdraw"
                          onClick={() => setDeleting(doc)}
                          disabled={busy}
                          aria-label={`Withdraw ${documentTitle(doc)}`}
                          className={SECONDARY_BUTTON}
                        >
                          <Undo2 className="h-4 w-4" aria-hidden="true" />
                          Withdraw
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {showUpload && (
        <UploadDocumentModal
          employeeId={employeeId}
          employeeName={employeeName}
          onBehalf={!isSelf}
          autoApproved={!isSelf && hasPermission('documents', 'approve')}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); load(); }}
        />
      )}

      {rejecting && (
        <CModal
          onClose={() => setRejecting(null)}
          labelledBy="employee-documents-reject-title"
          dismissible={acting !== rejecting.id}
          closeOnBackdrop={false}
          overlayClassName="bg-black/40"
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
          data-testid="employee-documents-reject-modal"
        >
          <form onSubmit={(e) => { e.preventDefault(); if (rejectReason.trim()) decide(rejecting, 'reject', rejectReason.trim()); }}>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 id="employee-documents-reject-title" className="text-base font-semibold text-gray-900">Reject document</h3>
              <p className="text-sm text-gray-500 mt-0.5 break-words">{documentTitle(rejecting)} ({rejecting.document_type})</p>
            </div>
            <div className="px-6 py-4">
              <label htmlFor="employee-documents-reject-reason" className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-danger-600">*</span>
              </label>
              <textarea
                id="employee-documents-reject-reason"
                data-testid="employee-documents-reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                maxLength={1000}
                required
                placeholder="The employee sees this, so say what to fix"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button type="button" onClick={() => setRejecting(null)} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50">
                Cancel
              </button>
              <button
                type="submit"
                data-testid="employee-documents-reject-confirm"
                disabled={!rejectReason.trim() || acting === rejecting.id}
                className="px-4 py-2 bg-danger-600 text-white text-sm rounded-md hover:bg-danger-700 disabled:opacity-60"
              >
                {acting === rejecting.id ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </form>
        </CModal>
      )}

      {deleting && (
        <CModal
          onClose={() => setDeleting(null)}
          labelledBy="employee-documents-delete-title"
          dismissible={acting !== deleting.id}
          overlayClassName="bg-black/40"
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
          data-testid="employee-documents-delete-modal"
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 id="employee-documents-delete-title" className="text-base font-semibold text-gray-900">
              {deletingIsWithdraw ? 'Withdraw document' : 'Delete document'}
            </h3>
          </div>
          <p className="px-6 py-4 text-sm text-gray-700 break-words">
            {deletingIsWithdraw
              ? `Withdraw ${documentTitle(deleting)} (${deleting.document_type})? HR will not review it, and the file is removed.`
              : `Delete ${documentTitle(deleting)} (${deleting.document_type})? The file is removed for good.`}
          </p>
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button type="button" onClick={() => setDeleting(null)} className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="button"
              data-testid="employee-documents-delete-confirm"
              onClick={() => remove(deleting)}
              disabled={acting === deleting.id}
              className="px-4 py-2 bg-danger-600 text-white text-sm rounded-md hover:bg-danger-700 disabled:opacity-60"
            >
              {deletingIsWithdraw
                ? (acting === deleting.id ? 'Withdrawing…' : 'Withdraw')
                : (acting === deleting.id ? 'Deleting…' : 'Delete')}
            </button>
          </div>
        </CModal>
      )}
    </section>
  );
};

export default EmployeeDocuments;
